import React, { memo, useState, useEffect } from "react";
import { HashLink as Link } from "react-router-hash-link";
import Helper from "../functions/Helper.js";
import BuyButtonV3 from "../components/BuyButtonV3.js";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "../actions/Actions.js";
import styled from "@emotion/styled";
import Popup from "../components/Popup.js";
import Modal from "react-bootstrap/Modal";

/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

const GridContainer = styled.div({
  "@media (min-width:576px)": {
    display: "grid",
    gridTemplateColumns: "4fr 8fr",
    gridTemplateRows: "auto 1fr",
    gridColumnGap: "6%",
  },
});

const Rating = ({ shoe, sortBy, userRating }) => {
  let ratingCategory;
  let ratingCaption = "";
  if (
    [
      "traction",
      "cushion",
      "materials",
      "support",
      "fit",
      "outdoor",
      "user_traction",
      "user_cushion",
      "user_materials",
      "user_support",
      "user_fit",
      "user_outdoor",
      "user_durability",
    ].includes(sortBy)
  ) {
    ratingCaption = sortBy.split("user_").pop();
    ratingCategory = sortBy;
  } else {
    ratingCategory = userRating ? "user_rating" : "rating";
  }

  return (
    <li style={{ marginBottom: 10 }}>
      <div style={{ display: "flex" }}>
        <div
          style={{
            display: "flex",
            padding: "0px 8px",
            alignItems: "center",
            minHeight: 30,
            fontSize: 20,
            lineHeight: "1",
            fontWeight: 600,
            backgroundColor: Helper.get_color(
              shoe[ratingCategory],
              ["outdoor", "user_outdoor"].includes(ratingCategory)
                ? "outdoor"
                : "bg"
            ),
          }}
        >
          <div style={{ color: "white" }}>
            {Helper.printRating(
              shoe[ratingCategory],
              ["outdoor", "user_outdoor"].includes(ratingCategory) ? 0 : 1
            )}
            {["outdoor", "user_outdoor"].includes(ratingCategory) && "%"}
          </div>
        </div>
        <div
          style={{
            fontSize: 16,
            flex: 1,
            lineHeight: "1.2",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            marginLeft: 8,
          }}
        >
          {ratingCaption + " "}rating based on&nbsp;
          <Link
            style={{ fontWeight: 600 }}
            to={"/shoe-reviews/" + shoe.url.toLowerCase() + "/"}
          >
            {userRating
              ? shoe.user_review_amount + " user reviews"
              : shoe.review_amount + " expert reviews"}
          </Link>
        </div>
      </div>
    </li>
  );
};

const getRatingText = (shoe, category) => {
  if (category === "outdoor") {
    return Helper.getOutdoorDescription(shoe[category]);
  } else if (category === "size" || category === "width") {
    return Helper.getSizeDescription(shoe[category], category, "short");
  } else if (category === "price" || category === "discount") {
    return "$" + Number(shoe[category]).toFixed(2);
  } else if (category === "weight_min") {
    return (
      (
        "~" +
        (Number(shoe.weight_min) + Number(shoe.weight_max)) / 2
      ).toString() + "g (US Size 9)"
    );
  } else {
    return shoe[category];
  }
};

const output_date = (date) => {
  date = Helper.getDateFromMysql(date);
  const monthNames = [
    "Jan.",
    "Feb.",
    "Mar.",
    "Apr.",
    "May",
    "Jun.",
    "Jul.",
    "Aug.",
    "Sep.",
    "Oct.",
    "Nov.",
    "Dec.",
  ];
  return monthNames[date.getMonth()] + " " + date.getFullYear();
};

const AdditionalText = ({ shoe, sortBy }) => {
  if (["style", "width", "size", "price", "weight_min"].includes(sortBy)) {
    let caption = {
      style: "Style of Cut",
      width: "Width",
      size: "Size",
      price: "List price",
      weight_min: "Weight",
    };
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          fontSize: 16,
          marginTop: -5,
          fontWeight: 600,
          color: "var(--grey-600)",
        }}
      >
        {caption[sortBy]}: {getRatingText(shoe, sortBy)}
      </div>
    );
  } else
    return (
      <div
        style={{
          fontSize: 16,
          marginTop: -5,
          fontWeight: 600,
          color: "var(--grey-600)",
        }}
      >
        released {output_date(shoe.release_date)}
      </div>
    );
};

function ShoeListWithPictures(props) {
  let { shoe } = props;
  const [showingPopup, setShowingPopup] = useState(false);

  let img_options =
    props.index > 2
      ? {
          "data-srcset": Helper.create_source_set(shoe.pictures[1]),
          className: "lazyload",
        }
      : {
          srcSet: Helper.create_source_set(shoe.pictures[1]),
        };

  let webp_options =
    props.index > 2
      ? {
          "data-srcset": Helper.create_source_set(
            shoe.pictures[1],
            undefined,
            "webp"
          ),
        }
      : {
          srcSet: Helper.create_source_set(shoe.pictures[1], undefined, "webp"),
        };

  let png_options =
    props.index > 2
      ? {
          "data-srcset": Helper.create_source_set(shoe.pictures[1]),
        }
      : {
          srcSet: Helper.create_source_set(shoe.pictures[1]),
        };

  return (
    <>
      <Modal
        dialogClassName="thg_popup_modal"
        onHide={() => {
          setShowingPopup(false);
        }}
        show={showingPopup}
      >
        <Popup
          prefix={"overview"}
          lastUpdate={"XXX"}
          hideModal={() => {
            setShowingPopup(false);
          }}
          shoe={shoe}
        />
      </Modal>

      <div
        css={{
          padding: props.index === 0 ? "0px 0px 40px 0px" : "40px 0px 40px 0px",
          borderBottom: "1px solid #eee",
          width: "100%",
        }}
      >
        <GridContainer>
          <div
            css={{ "@media (min-width:576px)": { gridColumn: 2, gridRow: 1 } }}
          >
            <div style={{ display: "flex", marginBottom: 5 }}>
              <h2
                className="thg_header thg_h4_size"
                style={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                <Link
                  className="thg_header thg_h4_size"
                  onMouseEnter={() => {
                    if (!shoe.hasOwnProperty("styles")) {
                      props.actions.fetch_colorways_and_styles(shoe.shoe_id);
                    }
                  }}
                  to={"/shoe-reviews/" + shoe.url.toLowerCase() + "/"}
                >
                  {shoe.shoe_name}
                </Link>
              </h2>

              {props.fav ? (
                <i
                  title="Remove from Favourites"
                  onClick={(x) => {
                    props.removeFav(shoe);
                  }}
                  className="fas fa-star"
                  style={{
                    fontSize: 22,
                    lineHeight: "30px",
                    marginLeft: 5,
                    color: "orange",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <i
                  title="Add to Favourites"
                  onClick={(x) => {
                    props.addFav(shoe);
                  }}
                  className="far fa-star"
                  style={{
                    fontSize: 22,
                    lineHeight: "30px",
                    marginLeft: 5,
                    color: "#cccccc",
                    cursor: "pointer",
                  }}
                />
              )}
            </div>
            <AdditionalText shoe={shoe} sortBy={props.sortBy} />
          </div>

          <div
            css={{
              "@media (min-width:576px)": {
                gridColumn: 1,
                gridRow: "1 / span 2",
              },
            }}
          >
            <div
              css={{
                position: "relative",
                "&:after": {
                  content: '""',
                  display: "block",
                  height: 0,
                  width: "100%",
                  /* 16:9 = 56.25% = calc(9 / 16 * 100%) */
                  paddingBottom:
                    (shoe.pictures[1].height / shoe.pictures[1].width) * 100 +
                    "%",
                },
                "& > *": {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                },
              }}
            >
              <Link to={"/shoe-reviews/" + shoe.url.toLowerCase() + "/"}>
                <picture>
                  <source
                    {...webp_options}
                    type="image/webp"
                    sizes="(max-width:576px) 80%, 270px"
                  />
                  <source
                    {...png_options}
                    type="image/png"
                    sizes="(max-width:576px) 80%, 270px"
                  />
                  <img
                    {...img_options}
                    sizes="(max-width:576px) 80%, 270px"
                    width={shoe.pictures[1].width}
                    height={shoe.pictures[1].height}
                    css={{
                      marginTop: 5,
                      width: "100%",
                      "@media (max-width:575px)": {
                        marginTop: 40,
                        width: "80%",
                        marginBottom: 20,
                      },
                      transform:
                        shoe.pictures[1].mirror == "1" && "scaleX(-1)",
                    }}
                    alt={shoe.shoe_name}
                  />
                </picture>
              </Link>
            </div>

            <div
              css={{
                "@media (min-width:576px)": {
                  marginLeft: 10,
                  textAlign: "left",
                  marginBottom: 0,
                },
                marginTop: 10,
                marginBottom: 40,
                marginRight: 10,
                textAlign: "right",
              }}
            >
              {/*<LazyLoad offset={500}>
              <LoadStyleTrigger fetchStyle={()=>{if(!shoe.hasOwnProperty("styles")){props.actions.fetch_colorways_and_styles(shoe.shoe_id)}}}/>
            </LazyLoad>*/}
              <BuyButtonV3
                onMouseEnter={() => {
                  if (!shoe.hasOwnProperty("styles")) {
                    props.actions.fetch_colorways_and_styles(shoe.shoe_id);
                  }
                }}
                linkId={"overview_button"}
                priceStyle={{ fontSize: 20 }}
                style={{
                  fontSize: 14,
                  padding: "0.5rem 1.2rem",
                  boxShadow: "none",
                }}
                onClick={() => {
                  setShowingPopup(true);
                }}
                shoe={shoe}
              />
            </div>
          </div>

          <ul
            css={{
              "@media (min-width:576px)": { gridColumn: 2, gridRow: 2 },
              marginTop: 20,
              marginBottom: 0,
              marginLeft: "20px!important",
            }}
          >
            <Rating
              userRating={props.userRating}
              shoe={shoe}
              sortBy={props.sortBy}
            />
            {shoe.short_summary !== "" && (
              <li style={{ fontSize: 16 }}>{shoe.short_summary}</li>
            )}
            {shoe.best_text !== "" && (
              <li style={{ fontSize: 16, marginTop: 10 }}>{shoe.best_text}</li>
            )}
          </ul>
        </GridContainer>
      </div>
    </>
  );
}

//export default memo(ShoeListWithPictures);

export default memo(
  connect(null, (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
  }))(ShoeListWithPictures)
);
